import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";

/**
 * Per-lead "why can't I access this?" data — inverse of the visibility
 * explainer. Powers the banner under Revoked → No Access on the lead
 * detail panel: tells a counsellor exactly which access is missing
 * (revoked assignment, stage not in role, branch unassigned, CF block,
 * soft delete) so they can act on it / escalate to the admin.
 *
 * Endpoint: GET /core/leads/:id/invisibility (admin-on-behalf via
 * `?asUser=<userId>`, otherwise returns reasons for the caller).
 */
export type LeadInvisibilityReasonKind =
  | "assignment_revoked"
  | "stage_not_in_role"
  | "branch_not_in_user"
  | "custom_field_blocked"
  | "soft_deleted"
  | "not_assigned"
  | "has_access";

export interface LeadInvisibilityReason {
  kind: LeadInvisibilityReasonKind;
  label: string;
  detail: string;
  meta?: Record<string, unknown>;
}

export interface LeadInvisibilityResult {
  leadId: number;
  userId: number;
  userName: string;
  hasAccess: boolean;
  reasons: LeadInvisibilityReason[];
}

export function useLeadInvisibility(
  leadId: number | null | undefined,
  options?: { asUser?: number; enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!leadId;
  return useQuery<LeadInvisibilityResult>({
    queryKey: ["lead-invisibility", leadId, options?.asUser ?? null],
    enabled,
    queryFn: async () => {
      const params = options?.asUser ? `?asUser=${options.asUser}` : "";
      const res = await axiosInstance.get(
        `/core/leads/${leadId}/invisibility${params}`,
      );
      return res.data as LeadInvisibilityResult;
    },
    // CF-gating / invisibility is driven by RBAC config, not live data, and
    // changes mid-session only on a permission change (which busts caches via
    // the access-refresh path). 30s re-hit the endpoint on every drawer open.
    staleTime: 3 * 60 * 1000,
  });
}
